import { Button } from "@web/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import type Webcam from "react-webcam";
import { useCameraCapture } from "./hooks/use-camera-capture";
import { useCameraStream } from "./hooks/use-camera-stream";
import { useSealCaptureGuidance } from "./hooks/use-seal-capture-guidance";
import { useSealSubmission } from "./hooks/use-seal-submission";
import { CameraCaptureView } from "./shared/camera-capture-view";
import { CameraError } from "./shared/camera-error";
import { CameraLoading } from "./shared/camera-loading";
import { LocalFileCaptureView } from "./shared/local-file-capture-view";
import { PhotoReviewView } from "./shared/photo-review-view";
import { QrCodeNotFoundError } from "./shared/qrcode-not-found-error";
import { SealPositionAlert } from "./shared/seal-position-alert";

export function CameraPage() {
	const useLocalFileCapture =
		import.meta.env.DEV && window.location.hostname === "localhost";
	const [modalOpen, setModalOpen] = useState(true);
	const [searchParams] = useSearchParams();
	const selo = searchParams.get("selo");

	const webcamRef = useRef<Webcam>(null);

	const stream = useCameraStream();
	const capture = useCameraCapture(webcamRef);
	const submission = useSealSubmission(selo);
	const sealGuidance = useSealCaptureGuidance({
		webcamRef,
		enabled:
			!useLocalFileCapture &&
			!modalOpen &&
			stream.isCameraReady &&
			!stream.cameraError &&
			!stream.cameraTimeoutError &&
			!capture.capturedImageUrl,
		onFinalCapture: capture.setCapturedImageBlob,
	});

	const retakePhoto = () => {
		capture.resetCapture();
		stream.resetForRetake();
		sealGuidance.reset();
		submission.reset();
	};

	const handleConfirm = () => {
		if (!capture.capturedImageUrl) return;
		void submission.confirmPhoto(capture.capturedImageUrl);
	};

	if (submission.isError) {
		if (submission.isQrCodeNotFoundError)
			return (
				<QrCodeNotFoundError
					isUsingQueryEntry={true}
					retakePhoto={retakePhoto}
				/>
			);

		return (
			<CameraError
				activeError={submission.error}
				isUsingQueryEntry={true}
				retakePhoto={retakePhoto}
			/>
		);
	}

	if (submission.isValidating || submission.isPending)
		return <CameraLoading imageUrl={capture.capturedImageUrl} />;

	return (
		<div className="fixed top-0 right-0 bottom-0 left-0 flex flex-1 flex-col overflow-hidden bg-black">
			<div className="absolute top-4 left-4 z-30">
				<Button
					variant="ghost"
					size="icon"
					className="rounded-full border border-white/10 bg-black/40 text-white backdrop-blur-md hover:bg-black/60"
					asChild
				>
					<Link to={`/tutorial/${selo}`}>
						<ArrowLeft className="h-5 w-5" />
					</Link>
				</Button>
			</div>

			{!capture.capturedImageUrl && useLocalFileCapture ? (
				<LocalFileCaptureView onSelect={capture.setCapturedImageBlob} />
			) : !capture.capturedImageUrl ? (
				<CameraCaptureView
					webcamRef={webcamRef}
					facingMode={stream.facingMode}
					isCameraReady={stream.isCameraReady}
					cameraError={stream.cameraError}
					cameraTimeoutError={stream.cameraTimeoutError}
					onCameraReady={stream.handleCameraReady}
					onCameraError={stream.handleCameraError}
					onRetryCamera={stream.retryCamera}
					showFlash={capture.showFlash}
					onCapture={capture.capturePicture}
					guidance={sealGuidance.guidance}
					countdown={sealGuidance.countdown}
				/>
			) : (
				<PhotoReviewView
					imageUrl={capture.capturedImageUrl}
					isValidating={submission.isValidating}
					onRetake={retakePhoto}
					onConfirm={handleConfirm}
				/>
			)}

			<SealPositionAlert open={modalOpen} onOpenChange={setModalOpen} />
		</div>
	);
}
